<template>
  <section class="risk-center">
    <div v-if="!leader" class="empty">请选择左侧监控龙头</div>
    <template v-else>
      <div class="hero">
        <div class="hero-main">
          <div class="name-line">
            <span class="name">{{ leader.name }}</span>
            <span class="code">{{ leader.code }}</span>
            <span class="pct" :class="pctClass(leader.changePct)">{{ formatPct(leader.changePct) }}</span>
          </div>
          <div class="tags">
            <span class="tag">{{ detail.cycleTag || '大周期' }}</span>
            <span class="tag muted">{{ detail.identityTag || '外部身份' }}</span>
            <span v-if="leader.boardStageLabel" class="tag">{{ leader.boardStageLabel }}</span>
          </div>
        </div>
        <button type="button" class="detail-link" disabled>风险详情 ›</button>
      </div>

      <div class="stage-card" :class="stageTone">
        <div class="stage-title">{{ detail.stageLabel }}</div>
        <div class="stage-caption">{{ detail.stageCaption }}</div>

        <div class="stepper">
          <div
            v-for="(st, i) in stages"
            :key="st.id"
            class="step"
            :class="stepClass(st, i)"
          >
            <div class="dot">
              <span v-if="isDone(st, i)">✓</span>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <div class="step-label">{{ st.label }}</div>
            <div v-if="i < stages.length - 1" class="bar" />
          </div>
        </div>

        <div class="repair-row">
          <div class="repair-box">
            <div class="rb-label">独立修复轨</div>
            <div class="rb-val">
              修复尝试 ≈ <b>{{ detail.repairAttempt.status }}</b>
              <span class="rb-time">{{ detail.repairAttempt.time }}</span>
            </div>
          </div>
          <div class="repair-box">
            <div class="rb-label">修复确认</div>
            <div class="rb-val muted">{{ detail.repairConfirm.status }}</div>
          </div>
        </div>

        <div class="stats">
          <div class="stat">
            <div class="k">本轮开始</div>
            <div class="v">{{ detail.stats.startDate }}</div>
          </div>
          <div class="stat">
            <div class="k">开始后严重损伤</div>
            <div class="v danger">{{ detail.stats.severeDamageCount }}次</div>
          </div>
          <div class="stat">
            <div class="k">最近一次损伤</div>
            <div class="v">{{ detail.stats.lastDamageDate }}</div>
          </div>
          <div class="stat">
            <div class="k">未修复观察跨度</div>
            <div class="v">{{ detail.stats.unrepairedSpanDays }}交易日</div>
          </div>
        </div>
      </div>

      <div class="intraday">
        <div class="intraday-h">
          <span>单日时点对比</span>
          <em>布局演示 · 引擎待接入</em>
        </div>
        <div class="slots">
          <div v-for="slot in detail.intradaySlots" :key="slot.time" class="slot">
            <div class="slot-time">{{ slot.time }}</div>
            <div class="slot-stage" :class="stageTone">{{ slot.stageLabel }}</div>
            <div class="slot-note">{{ slot.repairNote }}</div>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import riskDemo from '@/data/leaderCommandCenter/riskModeDemo.json'

export default {
  name: 'RiskCenterPanel',
  props: {
    leader: { type: Object, default: null },
    leaderIndex: { type: Number, default: 0 }
  },
  computed: {
    stages() {
      return riskDemo.stages || []
    },
    detail() {
      const base = Object.assign({}, riskDemo.defaultDetail)
      const presets = riskDemo.stagePresets || []
      const p = presets[this.leaderIndex % Math.max(presets.length, 1)] || {}
      const merged = Object.assign({}, base, p)
      merged.repairAttempt = Object.assign({}, base.repairAttempt, p.repairAttempt || {})
      merged.repairConfirm = Object.assign({}, base.repairConfirm, p.repairConfirm || {})
      merged.stats = Object.assign({}, base.stats, p.stats || {})
      merged.intradaySlots = (p.intradaySlots || base.intradaySlots || []).map((s) =>
        Object.assign({}, s, { stageLabel: merged.stageLabel || s.stageLabel })
      )
      return merged
    },
    stageTone() {
      const id = this.detail.activeStageId
      if (id === 'divergence') return 'tone-div'
      if (id === 'ebb') return 'tone-ebb'
      return 'tone-cont'
    },
    activeIndex() {
      const id = this.detail.activeStageId
      const i = this.stages.findIndex((s) => s.id === id)
      return i < 0 ? 1 : i
    }
  },
  methods: {
    isDone(st, i) {
      return i < this.activeIndex
    },
    stepClass(st, i) {
      if (i < this.activeIndex) return 'done'
      if (i === this.activeIndex) return 'active ' + this.stageTone
      return 'todo'
    },
    formatPct(v) {
      if (v == null || !Number.isFinite(Number(v))) return '—'
      const n = Number(v)
      const sign = n > 0 ? '+' : ''
      return sign + n.toFixed(2) + '%'
    },
    pctClass(v) {
      if (v == null || !Number.isFinite(Number(v))) return 'flat'
      if (Number(v) > 0) return 'up'
      if (Number(v) < 0) return 'down'
      return 'flat'
    }
  }
}
</script>

<style scoped lang="scss">
.risk-center {
  background: #fff;
  border: 1px solid #d7e4f6;
  border-radius: 12px;
  padding: 14px 16px;
  min-height: 0;
  overflow: auto;
}
.empty {
  padding: 48px;
  text-align: center;
  color: #90a4c0;
  font-size: 13px;
}
.hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}
.name-line {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}
.name { font-size: 20px; font-weight: 800; color: #1a2b3c; }
.code { font-size: 13px; color: #90a4c0; }
.pct {
  font-size: 22px;
  font-weight: 800;
  &.up { color: #e24b4b; }
  &.down { color: #1aae6f; }
  &.flat { color: #7b93b5; }
}
.tags { margin-top: 6px; display: flex; gap: 6px; flex-wrap: wrap; }
.tag {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef6ff;
  color: #1a4f8b;
  border: 1px solid #cfe0f8;
  &.muted { background: #f5f7fa; color: #7b93b5; border-color: #e4e9f2; }
}
.detail-link {
  border: 0;
  background: transparent;
  color: #1a73e8;
  font-size: 13px;
  font-weight: 600;
  opacity: 0.55;
  cursor: not-allowed;
  white-space: nowrap;
}
.stage-card {
  border: 1px solid #e8eef8;
  border-radius: 12px;
  padding: 16px;
  background: #fafcff;
  &.tone-cont { border-color: #f0d2a8; background: #fffaf3; }
  &.tone-div { border-color: #cfe0f8; background: #f3f8ff; }
  &.tone-ebb { border-color: #f0c4c4; background: #fff5f5; }
}
.stage-title {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.2;
}
.tone-cont .stage-title { color: #e67e22; }
.tone-div .stage-title { color: #1a73e8; }
.tone-ebb .stage-title { color: #c62828; }
.stage-caption {
  margin-top: 6px;
  font-size: 12px;
  color: #7b93b5;
}
.stepper {
  display: flex;
  align-items: flex-start;
  margin: 18px 0 16px;
  gap: 0;
}
.step {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  background: #e8eef8;
  color: #7b93b5;
  z-index: 1;
}
.step.done .dot { background: #1a73e8; color: #fff; }
.step.active.tone-cont .dot { background: #e67e22; color: #fff; }
.step.active.tone-div .dot { background: #1a73e8; color: #fff; }
.step.active.tone-ebb .dot { background: #c62828; color: #fff; }
.step-label {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #7b93b5;
}
.step.active .step-label,
.step.done .step-label { color: #1a2b3c; }
.bar {
  position: absolute;
  top: 13px;
  left: calc(50% + 18px);
  width: calc(100% - 36px);
  height: 2px;
  background: #e0e8f4;
}
.step.done .bar { background: #1a73e8; }
.repair-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;
}
.repair-box {
  border: 1px solid #e8eef8;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
}
.rb-label { font-size: 11px; color: #90a4c0; margin-bottom: 4px; }
.rb-val { font-size: 13px; color: #1a2b3c; }
.rb-val.muted { color: #7b93b5; }
.rb-time { margin-left: 8px; font-size: 11px; color: #90a4c0; }
.stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  border-top: 1px solid #e8eef8;
  padding-top: 12px;
}
.stat .k { font-size: 11px; color: #90a4c0; }
.stat .v { margin-top: 4px; font-size: 15px; font-weight: 700; color: #1a2b3c; }
.stat .v.danger { color: #c62828; }
.intraday { margin-top: 14px; }
.intraday-h {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #1a2b3c;
  em { font-style: normal; font-size: 11px; font-weight: 500; color: #90a4c0; }
}
.slots {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}
.slot {
  border: 1px solid #e8eef8;
  border-radius: 10px;
  padding: 10px 8px;
  background: #fafcff;
  text-align: center;
}
.slot-time { font-size: 12px; font-weight: 700; color: #35598c; }
.slot-stage {
  margin: 6px 0;
  font-size: 12px;
  font-weight: 700;
  &.tone-cont { color: #e67e22; }
  &.tone-div { color: #1a73e8; }
  &.tone-ebb { color: #c62828; }
}
.slot-note { font-size: 11px; color: #90a4c0; line-height: 1.35; }
@media (max-width: 1100px) {
  .stats { grid-template-columns: repeat(2, 1fr); }
  .slots { grid-template-columns: repeat(2, 1fr); }
}
</style>
